import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

/* ============================================================
   FETCH SAC CODE LIST (Pagination + Search + Filters + Ordering)
=============================================================== */
export const fetchSacCodes = createAsyncThunk(
  "sac-code/fetchSacCodes",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any[];
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      if (search) params.append("search", search.trim());
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      const filterPayload = FilterBuilder(filters ?? []);

      const body = {
        filters: filterPayload,
      };

      const response: any = await Axios.post(
        `${API_ENDPOINT?.MASTERS?.SAC_CODE?.LIST}?${params.toString()}`,
        body,
      );
      return {
        data: response.data.data || [],
        rowCount: response.data.count || 0,
      };
    } catch (error: any) {
      throw new Error("Failed to fetch SAC Code");
    }
  },
);

/* ============================================================
   FETCH SINGLE SAC CODE DETAILS
=============================================================== */
export const fetchSacCodeById = createAsyncThunk(
  "sac-code/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `${API_ENDPOINT?.MASTERS?.SAC_CODE?.DETAIL(id)}`,
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch SAC code details",
      );
    }
  },
);

/* ============================================================
   FETCH SAC CODE DROPDOWN LIST
=============================================================== */
export const fetchSacCodeByDropDown = createAsyncThunk(
  "SACCode/fetchByDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.MASTERS?.SAC_CODE?.DROPDOWN}`,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch dropdown list",
      );
    }
  },
);

/* ============================================================
   UPDATE SAC CODE
=============================================================== */
export const updateSacCode = createAsyncThunk(
  "sac-code/updateSacCode",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.put(
        `${API_ENDPOINT?.MASTERS?.SAC_CODE?.UPDATE(data.id)}`,
        data,
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update SAC code");
    }
  },
);

/* ============================================================
   TOGGLE SAC CODE STATUS (Active / Inactive)
=============================================================== */
export const updateStatusInSacCode = createAsyncThunk(
  "sac-code/updateStatusInSacCode",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.patch(
        `${API_ENDPOINT?.MASTERS?.SAC_CODE?.STATUS_UPDATE(data.id)}`,
        { is_active: data.is_active },
      );
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update SAC code status",
      );
    }
  },
);

/* ============================================================
   CREATE NEW SAC CODE
=============================================================== */
export const addSacCode = createAsyncThunk(
  "sac-code/addSacCode",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        `${API_ENDPOINT?.MASTERS?.SAC_CODE?.CREATE}`,
        data,
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to create SAC code");
    }
  },
);

/* ============================================================
   DELETE SAC CODE
=============================================================== */
export const deleteSacCode = createAsyncThunk(
  "sac-code/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      await Axios.delete(`${API_ENDPOINT?.MASTERS?.SAC_CODE?.DELETE(id)}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

/* ============================================================
   EXPORT CSV / EXCEL
=============================================================== */
export const exportSacCodeCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: {
    filterField: string;
    filterValue: any;
  }[];
}) => {
  const params = new URLSearchParams();

  params.append("export", "excel");

  if (search) params.append("search", search);

  // -----------------------------
  // Build structured filters body
  // -----------------------------
  const filterPayload = FilterBuilder(filters ?? []);

  const body = {
    filters: filterPayload,
  };

  const response = await Axios.post(
    `${API_ENDPOINT.MASTERS.SAC_CODE.EXPORT}?${params.toString()}`,
    body,
    { responseType: "blob" },
  );

  return {
    blob: response.data,
    filename: `${
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "SAC Code List"
    }_${new Date().toISOString().split("T")[0]}.csv`,
  };
};
